import { useEffect, useRef, type ReactNode } from 'react';
import { useScrollLock } from '../lib/scrollLock.js';

/**
 * Accessible modal — ESC to close (prototype parity), backdrop click to dismiss,
 * role="dialog" + aria-modal, and focus moved into the dialog on open. ehr#181: the page
 * behind is frozen while it is open (the ehr#231 scroll lock).
 *
 * calendar#38: focus moves into the dialog once, when it opens. It used to re-run whenever
 * `onClose` changed, and a parent that passes a new function each render (the CPT codes modal's
 * requestClose) re-rendered on every keystroke, so the dialog took focus back after each letter.
 * Escape reads the latest `onClose` through a ref.
 */
export function Modal({
  title,
  onClose,
  children,
  actions,
  className,
  hooks,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
  /** ehr#741: data attributes (each set to "") on the dialog, its close X and its footer. */
  hooks?: { root?: string; close?: string; foot?: string };
}) {
  const hook = (name?: string) => (name ? { [name]: '' } : {});
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  useScrollLock(true);

  useEffect(() => {
    closeRef.current = onClose;
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className={className ? `modal modal-std ${className}` : 'modal modal-std'}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={ref}
        onClick={(e) => e.stopPropagation()}
        {...hook(hooks?.root)}
      >
        <div className="modal-std-head">
          <h3>{title}</h3>
          <button
            type="button"
            className="modal-close"
            aria-label="Close"
            onClick={onClose}
            {...hook(hooks?.close)}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="modal-std-body">{children}</div>
        {actions ? (
          <div className="modal-std-foot" {...hook(hooks?.foot)}>
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  );
}
